import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import EventEHRSimpleCard from "../../../component-lib/Molecules/card/EventEHRSimpleCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import EventEHRItemListCard from "../../../component-lib/Molecules/card/EventEHRItemListCard";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import { useNavigate } from "react-router-dom";
import useHasOverflow from "../../../custom-hooks/HasOverflow";
import { useAutoRefresh } from "../../../custom-hooks/useAutoRefresh";

import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getLoginUserData, getLoginUserId } from "../../../utils/getToken";
import { capitalizeName } from "../../../utils/capitalizeName";
import {
  destructStatusData,
  getStatusBackground,
  getStatusDotColor,
  getStatusTextColor,
} from "../../../utils/statusConstants";
import TooltipForTable from "../../../component-lib/Molecules/tooltip/TooltipForTable";
import ViewVisitDialog from "./ViewVisitDialog";
import { getDataForFacilities, getFacilityRooms } from "../DashboardSaga";
import { componentKey } from "../DashboardSlice";

interface DashboardScreenProps {
  defaultData?: any;
}

const FacilityDashboard = ({ defaultData }: DashboardScreenProps) => {
  const { facilityData, facilityRooms } = useSelector(
    (state: any) => state[componentKey]
  );

  // Show all facility rooms. Waiting & Expected rooms are pinned to the top
  // (Waiting first, then Expected); all other rooms follow in API order.
  const roomRank = (room: any) =>
    room?.isWaitingRoom ? 0 : room?.isExpectedRoom ? 1 : 2;
  const roomAssignmentsData = [...(facilityRooms ?? [])].sort(
    (a: any, b: any) => roomRank(a) - roomRank(b)
  );

  const loginUserData: any = getLoginUserData();
  const parsedLoginUserData = JSON.parse(loginUserData);
  const loggedInUserName = capitalizeName(parsedLoginUserData?.name);
  const dispatch = useDispatch();
  const divRef = useRef<HTMLDivElement>(null);
  const hasOverflow = useHasOverflow(divRef);
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [selectedTask, setSelectedTask] = useState<any>({});
  const [openFacilityViewDialog, setOpenFacilityViewDialog] = useState(false);

  const parseLoginUserId: any = getLoginUserId();
  const loginUserId = JSON.parse(parseLoginUserId);

  const dashboardData = {
    totalVisit: facilityData?.totalVisit || 0,
    EMSVisit: facilityData?.emsVisit || 0,
    discharged: facilityData?.discharged || 0,
    transferToJHC: facilityData?.transferToJHC || 0,
    transferToOffProperty: facilityData?.transferOffProperty || 0,
    unsigned: facilityData?.unsigned || 0,
  };
  const {
    totalVisit,
    EMSVisit,
    discharged,
    transferToJHC,
    transferToOffProperty,
    unsigned,
  } = dashboardData;

  // Dashboard simple cardS
  const cardsData = [
    { title: "Total Visits", value: totalVisit || "0", className: "" },
    { title: "EMS Visits", value: EMSVisit || "0", className: "" },
    { title: "Discharge", value: discharged || "0", className: "" },
    { title: "Expected", value: transferToJHC || "0", className: "" },
    {
      title: "Transfer Off Property",
      value: transferToOffProperty || "0",
      className: "",
    },
    {
      title: "Unsigned Visits",
      value: unsigned || "0",
      className: "font-medium text-primary-main cursor-pointer",
    },
  ];

  //   // Recent patient list data
  const listItemsData = facilityData?.recentpatient?.map((data: any) => ({
    title: `${data?.participant?.firstName || ""} ${data?.participant?.lastName || ""}`,
    subTitle: "BSA ID",
    valueOfSubTitle: data?.participant?.bsaId || "",
    imgURL: data?.participant?.profilePicture,
    id: data?.id,
  }));

  // To Do Task list data
  const todoTaskData = facilityData?.taskDetails?.map((data: any) => ({
    title: data?.taskType || "-",
    totalTask: 3,
    data: data,
  }));

  // RECENT VISIT COLUMN DATA
  const recentVisitColumns = [
    {
      field: "bsaId",
      label: <div>BSA ID</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal common-text-ellipse-style">
            {row?.bsaId}
          </p>
        );
      },
    },
    {
      field: "patientName",
      label: <div>Patient Name</div>,
      renderLogic: (row: any) => {
        return (
          <p className="font-medium text-neutral-90 cursor-pointer">
            {row?.patientName}
          </p>
        );
      },
    },
    {
      field: "gender",
      label: <div>Gender</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.gender}</p>;
      },
    },
    {
      field: "visitType",
      label: <div>Visit Type</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.visitType}</p>;
      },
    },
    {
      field: "clinicLocation",
      label: <div>Unit Number</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.clinicLocation}</p>
        );
      },
    },
    {
      field: "chiefComplaint",
      label: <div>Chief Complaint</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            <TooltipForTable label={row?.chiefComplaint} />
          </p>
        );
      },
    },
    {
      field: "department",
      label: <div>Department</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.department}</p>;
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any) => {
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <span
              className="font-weight-400 font-size-12px flex gap-1 items-center"
              style={{
                color: getStatusTextColor(row?.status),
                background: getStatusBackground(row?.status),
                borderRadius: "46px",
                padding: "4px 12px",
              }}
            >
              <span
                className="w-2 h-2"
                style={{
                  background: getStatusDotColor(row?.status),
                  borderRadius: "50%",
                }}
              ></span>
              {destructStatusData(row?.status)}
            </span>
          </div>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown
                options={[
                  {
                    label: "View Visit Details",
                    onClickCb: () => {
                      setSelectedTask(row);
                      setOpenFacilityViewDialog(true);
                    },
                  },
                ]}
              />
            </div>
          </>
        );
      },
    },
  ];

  // RECENT VISIT ROW DATA
  const recentVisitRowData = facilityData?.recentVisit?.visit?.map(
    (data: any) => ({
      id: data?.id,
      patientId: data?.patientId,
      typeOfCare: data?.typeOfCare,
      bsaId: data?.patient?.participant?.bsaId || "-",
      patientName: `${data?.patient?.participant?.firstName || "-"} ${data?.patient?.participant?.lastName || "-"}`,
      gender: data?.patient?.participant?.gender || "-",
      visitType: data?.type === "WALK_In_Visit" ? "Walk In" : "EMS",
      clinicLocation: data?.patient?.participant?.baseCamp?.unitNumber || "",
      status: data?.status || "-",
      chiefComplaint: data?.chiefComplaints || "-",
      department: data?.typeOfCare || "-",
    })
  );
  useEffect(() => {
    setPageNumber(1);
  }, [defaultData?.value]);

  useEffect(() => {
    const payload = {
      payload: {
        facilityId: defaultData?.value,
        pageNumber: pageNumber,
        limit: rowPerPage,
      },
    };
    dispatch(getDataForFacilities(payload));
  }, [defaultData, pageNumber, rowPerPage]);

  // Fetch room assignments for the selected facility (refetch when it changes).
  useEffect(() => {
    dispatch(getFacilityRooms({ facilityId: defaultData?.value || undefined }));
  }, [defaultData?.value]);

  // Auto-refresh: silently re-fetch this facility's dashboard data + rooms every
  // 30s (no full-screen spinner), preserving the current page/page-size. Paused
  // while the facility view dialog is open; tab-hidden pausing is in the hook.
  useAutoRefresh(
    () => {
      dispatch(
        getDataForFacilities({
          payload: {
            facilityId: defaultData?.value,
            pageNumber: pageNumber,
            limit: rowPerPage,
          },
          silent: true,
        })
      );
      dispatch(
        getFacilityRooms({
          facilityId: defaultData?.value || undefined,
          silent: true,
        })
      );
    },
    { intervalMs: 30000, enabled: !!defaultData?.value && !openFacilityViewDialog }
  );

  return (
    <>
      <div
        ref={divRef}
        className={`overflow-auto h-[calc(100dvh-200px)] ${hasOverflow ? "pr-2" : "0"}`}
      >
        {/* Dashboard cards */}
        <div className="flex w-full gap-3 mb-4">
          <EventEHRSimpleCard cardData={cardsData} />
        </div>

        {/* Recent Patient Card and Room Assignments */}
        <div className="flex flex-row gap-3 items-stretch">
          {
            <EventEHRItemListCard
              cardTitle="Recent Patients"
              listItemsData={listItemsData}
              itemsActionButtonName=""
              noDataDataFoundMessage="No Recent Patient Found "
            />
          }
          {/* Room Assignments (replaces the old To Do Tasks card) */}
          <div className="border-1px-solid-D2D2D2 rounded w-full flex flex-col">
            <div className="flex justify-between items-center m-3">
              <div className="text-sm font-semibold text-[#565656]">
                Room Assignments
              </div>
            </div>
            <div className="px-3 pb-3 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              {roomAssignmentsData?.length ? (
                roomAssignmentsData.map((room: any) => (
                  <div
                    key={room?.id}
                    className="border border-[#E8E8E8] rounded-md p-2 mb-2"
                  >
                    <div className="flex justify-between items-center gap-2">
                      <span
                        className="text-sm font-semibold text-[#393939] truncate"
                        title={room?.name}
                      >
                        {room?.name}
                        {room?.roomNumber ? ` (#${room?.roomNumber})` : ""}
                      </span>
                      <span className="text-xs text-[#727272] whitespace-nowrap">
                        {room?.occupiedCount ??
                          room?.roomAssignments?.length ??
                          0}
                        {room?.capacity ? `/${room?.capacity}` : ""}
                      </span>
                    </div>
                    <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                      {room?.roomAssignments?.length ? (
                        room.roomAssignments.map((assignment: any) => {
                          const patient = assignment?.patient;
                          const provider = patient?.assignedProvider;
                          const patientName =
                            `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim() ||
                            "-";
                          const providerName = provider
                            ? `${provider?.firstName || ""} ${provider?.lastName || ""}`.trim()
                            : "-";
                          // previousAssignedProvider can be null.
                          const previousProvider =
                            patient?.previousAssignedProvider;
                          const previousProviderName = previousProvider
                            ? `${previousProvider?.firstName || ""} ${previousProvider?.lastName || ""}`.trim()
                            : "";
                          return (
                            <div
                              key={assignment?.id}
                              className="flex flex-col bg-[#F8F8F8] rounded px-2 py-1"
                            >
                              <div className="flex items-center gap-1 text-xs">
                                <span className="text-[#9A9A9A] whitespace-nowrap">
                                  Patient:
                                </span>
                                <span
                                  className="text-[#393939] font-medium truncate"
                                  title={patientName}
                                >
                                  {patientName}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 text-[11px]">
                                <span className="text-[#9A9A9A] whitespace-nowrap">
                                  Assigned Provider:
                                </span>
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={
                                    <div className="flex flex-col gap-0.5">
                                      <span>
                                        Current: {providerName || "-"}
                                      </span>
                                      <span>
                                        Previous:{" "}
                                        {previousProviderName || "None"}
                                      </span>
                                    </div>
                                  }
                                >
                                  <span className="text-[#727272] truncate cursor-default">
                                    {providerName}
                                  </span>
                                </Tooltip>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <span className="text-xs text-[#BCBCBC]">
                          No patients assigned
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-[#BCBCBC] text-sm py-6">
                  No room assignments found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Visits Table Data */}
        <div className="mt-4">
          <Heading
            title="Recent Visits"
            customStyle={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#565656",
            }}
          />
          <div className="mt-2">
            <Table
              columns={recentVisitColumns}
              rows={recentVisitRowData}
              noDataFoundMessage={"No recent visit found"}
              paginationProps={{
                isPagination: true,
                page: pageNumber,
                totalPages: facilityData?.recentVisit?.totalPages,
                totalCount: facilityData?.recentVisit?.total,
                limit: rowPerPage,
                rowsPerPageOptions: [10, 25, 50],
                onPageChange: (page: any) => {
                  setPageNumber(page);
                },
                handleChangeRowsPerPage: (rowPerPage: any) => {
                  setRowPerPage(rowPerPage);
                  setPageNumber(1);
                },
              }}
            />
          </div>
        </div>
      </div>
      {openFacilityViewDialog && (
        <ViewVisitDialog
          dialogHeading="View Visit Details"
          handleClose={() => setOpenFacilityViewDialog(false)}
          visitData={selectedTask}
          isOpen={openFacilityViewDialog}
        />
      )}
    </>
  );
};

export default FacilityDashboard;
